import { Autocomplete, Box, CircularProgress, Collapse, FormControl, FormHelperText, InputLabel, Link, MenuItem, Select, SelectChangeEvent, Stack, TextField, Typography } from '@mui/material'
import { FunctionComponent, ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react"
import { buildCategoryFilterQueryString, findCategoryByDisplayName } from '../../../../../shared/utils/emission-filter-logic-utils'
import CategoryService from '../../../../../shared/services/category/category.service'
import { IGroupWithCategory } from '../../../../../shared/services/group/group.model';
import { sortGroupsByGhgCategory, formatGroupLabel } from '../../../../../shared/constants/ghg-category';
import { ICategory, IFactorSearchResult, IFactorSearchUnit } from '../../../../../shared/services/category/category.model';
import { IConversionSource } from '../../../../../shared/services/conversion-source/conversion-source.model';
import GroupService from '../../../../../shared/services/group/group.service';
import ConversionSourceService from '../../../../../shared/services/conversion-source/conversion-source.service';
import { UserDetailsContext } from '../../../../../context/user-details/user-details';
import { useDebounce } from '../../../../../hooks/use-debounce-hook';
import { getCategoryDisplayName } from '../../../../../shared/utils/category-utils';

/** Category filter endpoints require UUID `groupId` — never send the literal string `"null"`. */
const UUID_RE_FILTER = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isValidUuid(id: string | undefined | null): id is string {
  return typeof id === 'string' && UUID_RE_FILTER.test(id);
}

// ── Search-first picker support ────────────────────────────────────────────────
// A recently chosen factor, stored per organisation in localStorage. The conversion id
// is deliberately NOT stored: ids are year-specific, so a recent pick is re-resolved
// against the current source, category and factor year before it seeds anything.
interface IRecentFactorSelection {
  detailLevelOne: string;
  detailLevelTwo: string;
  detailLevelThree: string;
  detailLevelFour: string;
  detailLevelFive: string;
  measurementUnitName: string;
  scope: string | null;
  conversionSourceId: string;
  categoryId: string;
  groupId: string;
  savedAt: number;
}

interface IFactorSearchOption {
  key: string;
  group: 'Recently used' | 'Matches' | 'Current selection';
  pathLabel: string;
  metaLabel: string;
  label: string;
  result?: IFactorSearchResult;
  recent?: IRecentFactorSelection;
}

const FACTOR_SEARCH_DEBOUNCE_MS = 400;
const FACTOR_SEARCH_MIN_CHARS = 2;
const RECENT_FACTORS_STORED = 10;
const RECENT_FACTORS_SHOWN = 5;

// Same whitespace-and-case tolerance the backend applies to detail levels.
function normaliseFactorText(value?: string | null): string {
  return String(value ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
}

/**
 * The units an activity offers. A server that predates the split returns one result per
 * factor row with its single unit in measurementUnit, so fall back to that and the
 * behaviour is exactly as it was: one unit, filled in on selection.
 */
/**
 * The unit choice, handed to a parent that wants to place it itself (the manual entry form
 * puts it beside the amount, because that is where the user is reading the figure off a
 * bill). onChange goes straight back into the cascade's own handler, so the unit resolves
 * the conversion exactly as the inline select does.
 */
export interface IUnitChoice {
  options: { id: string, name: string }[];
  value: string;
  disabled: boolean;
  onChange: (unitName: string) => void;
  onOpen: () => void;
}

function factorResultUnits(result: IFactorSearchResult): IFactorSearchUnit[] {
  if (result.units && result.units.length > 0) return result.units;
  if (result.measurementUnit) {
    return [{ id: result.measurementUnit.id, name: result.measurementUnit.name, conversionId: result.id }];
  }
  return [];
}

function factorPathText(levels: Array<string | null | undefined>): string {
  return levels
    .filter((level): level is string => typeof level === 'string' && Boolean(level.trim()))
    .join(' · ');
}

function recentFactorKey(entry: IRecentFactorSelection): string {
  return [
    entry.conversionSourceId, entry.categoryId, entry.groupId,
    entry.detailLevelOne, entry.detailLevelTwo, entry.detailLevelThree,
    entry.detailLevelFour, entry.detailLevelFive, entry.measurementUnitName,
  ].map(normaliseFactorText).join('|');
}

function recentFactorsStorageKey(organisationId: string): string {
  return `emissionFactorRecents:${organisationId}`;
}

function readRecentFactors(organisationId?: string): IRecentFactorSelection[] {
  if (!organisationId) return [];
  try {
    const raw = window.localStorage.getItem(recentFactorsStorageKey(organisationId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function storeRecentFactor(organisationId: string | undefined, entry: IRecentFactorSelection): void {
  if (!organisationId) return;
  try {
    const existing = readRecentFactors(organisationId)
      .filter(item => recentFactorKey(item) !== recentFactorKey(entry));
    window.localStorage.setItem(
      recentFactorsStorageKey(organisationId),
      JSON.stringify([entry, ...existing].slice(0, RECENT_FACTORS_STORED)),
    );
  } catch {
    // Recents are a convenience; unavailable storage must never break the picker.
  }
}

export interface IEmissionFilterChosenValue {
  detailLevelOne: string;
  detailLevelTwo: string;
  detailLevelThree: string;
  detailLevelFour: string;
  detailLevelFive: string;
  measurementUnitName: string;
  returnId?: string;
}

export interface IEmissionFilterInputValues {
  detailLevelOne?: string;
  detailLevelTwo?: string;
  detailLevelThree?: string;
  detailLevelFour?: string;
  detailLevelFive?: string;
  measurementUnitName?: string;
  returnId?: string;
}

export const EmissionsFilterLogic: FunctionComponent<{
  direction?: 'row' | 'column',
  inputValues?: IEmissionFilterInputValues,
  formik?: any,
  categoryId?: string;
  groupId?: string;
  defaultConversionSourceId?: string;
  // reportPeriodId?: string,
  calendarYear?: string,
  onValueChange?: (value: IEmissionFilterChosenValue) => void;
  size?: 'small' | 'medium',
  isEdit?: boolean,
  isInputForm?: boolean,
  allowGroupSelect?: boolean,
  allowConversionSourceSelect?: boolean,
  allowCategorySelect?: boolean,
  disableFields?: boolean,
  // Pre-answer any level whose option list has exactly one entry (on by default).
  // A single-factor category like Water supply otherwise asks three questions that
  // each have only one answer. It only fires when the level's value is still empty
  // and never in edit mode, so pre-seeded selections are left alone. Pass false to
  // keep deliberate blanks.
  autoSelectSingleOptions?: boolean,
  // Shown when the first-level factor query completes with no options. Without it the
  // component renders nothing at all, which reads as a broken form. Has a built-in
  // default; pass a string to override the copy.
  emptyStateMessage?: string,
  // Search-first mode (on by default): a single search box that finds a factor across
  // every detail level and the unit in one query, with the level cascade kept as a
  // collapsed "Browse by category instead" fallback. Pass false for a call site where
  // searching genuinely makes no sense; the cascade then renders exactly as before.
  enableSearch?: boolean,
  // In edit mode the pre-seeded factor renders as text with a "Change" affordance.
  // Call sites that already wrap this component in their own confirmed-selection card
  // (the survey factor picker) pass false so the user is not asked to click twice.
  showSelectionSummary?: boolean,
  // Hand the unit choice to the parent instead of rendering it in the cascade. The manual
  // entry form uses this to sit the unit next to the amount; every other call site leaves
  // it off and the select renders inline exactly as before.
  externalUnitSelect?: boolean,
  // Must be referentially stable (a useState setter is ideal). It is called only when the
  // choice actually changes, never on every render.
  onUnitChoiceChange?: (choice: IUnitChoice | null) => void,
  // Rendered at the right-hand end of the "Browse by category instead" row. The manual entry
  // form puts the factor source there: it belongs beside the factor it qualifies, not in a
  // line of its own underneath.
  browseRowTrailing?: ReactNode,
  prefetchedFilters?: {
    d1?: string[];
    d2?: string[];
    d3?: string[];
    d4?: string[];
    d5?: string[];
    units?: any[];
  };
}> = ({
  direction = 'row',
  inputValues,
  formik,
  categoryId,
  groupId,
  defaultConversionSourceId,
  // reportPeriodId,
  calendarYear,
  onValueChange,
  size = 'medium',
  isEdit = false,
  isInputForm = false,
  allowGroupSelect = false,
  allowConversionSourceSelect = false,
  allowCategorySelect = false,
  disableFields = false,
  autoSelectSingleOptions = true,
  emptyStateMessage = 'No emission factors match this combination. Try a different conversion factor source or category.',
  enableSearch = true,
  showSelectionSummary = true,
  externalUnitSelect = false,
  onUnitChoiceChange,
  browseRowTrailing,
  prefetchedFilters,
}) => {
    //Services
    const categoryService = new CategoryService();
    const groupService = new GroupService()
    const conversionSourceService = new ConversionSourceService();

    //State
    const [initialValuesSet, setInitialValuesSet] = useState<boolean>(false);
    const [detailLevelOne, setDetailLevelOne] = useState<string>();
    const [detailLevelTwo, setDetailLevelTwo] = useState<string>();
    const [detailLevelThree, setDetailLevelThree] = useState<string>();
    const [detailLevelFour, setDetailLevelFour] = useState<string>();
    const [detailLevelFive, setDetailLevelFive] = useState<string>();
    const [measurementUnitName, setMeasurementUnitName] = useState<string>();
    // Measurement unit ids are UUID strings (the filter endpoints and formik both carry them as such).
    const [measurementUnitId, setMeasurementUnitId] = useState<string>();
    const [returnId, setReturnId] = useState<string>();
    const [detailLevelOneFilters, setDetailLevelOneFilters] = useState<string[]>(prefetchedFilters?.d1 ?? []);
    const [detailLevelTwoFilters, setDetailLevelTwoFilters] = useState<string[]>(prefetchedFilters?.d2 ?? []);
    const [detailLevelThreeFilters, setDetailLevelThreeFilters] = useState<string[]>(prefetchedFilters?.d3 ?? []);
    const [detailLevelFourFilters, setDetailLevelFourFilters] = useState<string[]>(prefetchedFilters?.d4 ?? []);
    const [detailLevelFiveFilters, setDetailLevelFiveFilters] = useState<string[]>(prefetchedFilters?.d5 ?? []);
    const [measurementUnitNameFilters, setMeasurementUnitNameFilters] = useState<any[]>(prefetchedFilters?.units ?? []);
    // Only true once a first-level fetch has resolved, so the empty-state message never
    // shows while loading or after a failed request.
    const [detailLevelOneLoaded, setDetailLevelOneLoaded] = useState<boolean>(false);
    // First-level fetches overlap (two fetch sites, plus refires on category switches);
    // only the response for the most recently issued query may write state, or a slow
    // stale response could blank the options and mis-show the empty-state message.
    const latestDetailLevelOneRequestRef = useRef<string>('');
    const [groups, setGroups] = useState<IGroupWithCategory[]>()
    const [categories, setCategories] = useState<ICategory[]>()
    const [conversionSources, setConversionSources] = useState<IConversionSource[]>([]);
    const [conversionSourceId, setConversionSourceId] = useState<string>()
    const [selectedGroupId, setSelectedGroupId] = useState<string>();
    const [selectedCategoryId, setSelectedCategoryId] = useState<string>()
    const [selectedCategoryName, setSelectedCategoryName] = useState<string>()

    // ── Search-first mode state ──
    const userDetailsContext = useContext(UserDetailsContext);
    const organisationId = userDetailsContext?.userDetails?.organisation?.id;
    const [factorSearchInput, setFactorSearchInput] = useState<string>('');
    const [factorSearchResults, setFactorSearchResults] = useState<IFactorSearchResult[]>([]);
    const [factorSearchLoading, setFactorSearchLoading] = useState<boolean>(false);
    const [factorSearchHasMore, setFactorSearchHasMore] = useState<boolean>(false);
    /** A finished selection that has no factor in the current year (usually after a year change). */
    const [selectionUnavailableForYear, setSelectionUnavailableForYear] = useState<boolean>(false);
    /** The search space the single-activity auto-select has already fired for. */
    const autoSelectedSpaceRef = useRef<string | null>(null);
    const [selectedFactorOption, setSelectedFactorOption] = useState<IFactorSearchOption | null>(null);
    const [browseLevelsOpen, setBrowseLevelsOpen] = useState<boolean>(false);
    const [changeSelectionRequested, setChangeSelectionRequested] = useState<boolean>(false);
    const [recentFactorUnavailable, setRecentFactorUnavailable] = useState<string | undefined>();
    const [recentsVersion, setRecentsVersion] = useState<number>(0);
    // Only the response for the most recently issued search may write state.
    const latestFactorSearchRef = useRef<string>('');
    // Same guard for the returnId derivation and for recent-pick re-resolution: two quick
    // consecutive picks must not let the slower response overwrite the newer selection.
    const latestReturnIdRequestRef = useRef<string>('');
    const latestRecentResolveRef = useRef<string>('');
    // True from a search pick until the user re-walks the cascade or the query identity
    // changes; the single-option auto-fill must never run over an active search selection.
    const searchSelectionActiveRef = useRef<boolean>(false);
    // Set when the user empties the Emission factor field with its clear button. Clearing is how
    // you start choosing again, so the single-option cascade fill has to stay out of the way until
    // the user picks something; otherwise it walks straight back down and refills what was cleared.
    const factorClearedRef = useRef<boolean>(false);

    useEffect(() => {
      conversionSourceService.getAll(true, 'conversion')
        .then(response => {
          setConversionSources(response)
        })
        .catch(err => console.log(err))
    }, [])

    useEffect(() => {
    }, [categoryId, groupId, defaultConversionSourceId, conversionSourceId, isEdit])

    useEffect(() => {
      if (!groups || !selectedGroupId) return;
      const matchedGroup = groups.find(g => g.id === selectedGroupId);
      if (!matchedGroup) return;
      if (!categories) setCategories(matchedGroup.categories ?? []);
      if (selectedCategoryId && !selectedCategoryName) {
        const cat = matchedGroup.categories?.find(c => c.id === selectedCategoryId);
        if (cat) setSelectedCategoryName(cat.name || '');
      }
    }, [groups, selectedGroupId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      const conversionResolved = defaultConversionSourceId || conversionSourceId;
      if (!conversionResolved) return;
      groupService.getGroupsWithCategories(false)
        .then(response => {
          setGroups(response);
          if (groupId) {
            const newGroup = response?.find(group => group.id === groupId);
            setSelectedGroupId(newGroup?.id);
            setCategories(newGroup?.categories);
            setSelectedCategoryId('');
            if (formik && newGroup?.id) {
              formik.setFieldValue('groupId', newGroup.id);
            }
          }
        });
    }, [defaultConversionSourceId, conversionSourceId, groupId])

    useEffect(() => {
      // In edit mode, preserve existing values when category/conversion/group load.
      if (initialValuesSet && !isEdit) {
        setDetailLevelOne('')
        setDetailLevelTwo('');
        setDetailLevelThree('')
        setDetailLevelFour('')
        setDetailLevelFive('')
        setMeasurementUnitName('')
        setMeasurementUnitId(undefined)
        setReturnId('')
        setDetailLevelOneFilters([])
        setDetailLevelTwoFilters([]);
        setDetailLevelThreeFilters([])
        setDetailLevelFourFilters([])
        setDetailLevelFiveFilters([])
        setMeasurementUnitNameFilters([])
        if (formik) {
          formik.setFieldValue('detailLevelOne', '')
          formik.setFieldValue('detailLevelTwo', '')
          formik.setFieldValue('detailLevelThree', '')
          formik.setFieldValue('detailLevelFour', '')
          formik.setFieldValue('detailLevelFive', '')
          formik.setFieldValue('measurementUnitName', '')
          formik.setFieldValue('measurementUnitId', '')
          formik.setFieldValue('returnId', '')
        }
        getFilterValues('detailLevelOne')
      }
    }, [categoryId, groupId, defaultConversionSourceId])

    // Seed local state from formik (edit dialog) or from inputValues
    useEffect(() => {
      if (!isEdit) {
        // A fresh picker must not inherit a stale factor, which is what this early return is for.
        // Where it sits is another matter: a parent that opens the picker on a record already
        // holding a source, group and category (the edit dialog's category change) has to be able
        // to say so, or the user re-answers questions the record has answered. Seed the context,
        // never the choice.
        const seed = formik?.values || {};
        if (seed['conversionSourceId']) setConversionSourceId(seed['conversionSourceId']);
        if (seed['groupId']) setSelectedGroupId(seed['groupId']);
        if (seed['categoryId']) setSelectedCategoryId(seed['categoryId']);
        setInitialValuesSet(true);
        return;
      }
      if (formik) {
        const v = formik.values || {};

        if (v['detailLevelOne']) setDetailLevelOne(v['detailLevelOne']);
        if (v['detailLevelTwo']) setDetailLevelTwo(v['detailLevelTwo']);
        if (v['detailLevelThree']) setDetailLevelThree(v['detailLevelThree']);
        if (v['detailLevelFour']) setDetailLevelFour(v['detailLevelFour']);
        if (v['detailLevelFive']) setDetailLevelFive(v['detailLevelFive']);
        if (v['measurementUnitName']) setMeasurementUnitName(v['measurementUnitName']);
        if (v['measurementUnitId']) setMeasurementUnitId(v['measurementUnitId']);
        if (v['categoryId']) setSelectedCategoryId(v['categoryId']);
        if (v['groupId']) setSelectedGroupId(v['groupId']);
        if (v['conversionSourceId']) setConversionSourceId(v['conversionSourceId']);
        setInitialValuesSet(true);
        return;
      }
      if (inputValues) {

        Object.entries(inputValues).forEach(value => {
          if (value[0] === 'detailLevelOne') setDetailLevelOne(value[1]);
          else if (value[0] === 'detailLevelTwo') setDetailLevelTwo(value[1]);
          else if (value[0] === 'detailLevelThree') setDetailLevelThree(value[1]);
          else if (value[0] === 'detailLevelFour') setDetailLevelFour(value[1]);
          else if (value[0] === 'detailLevelFive') setDetailLevelFive(value[1]);
          else if (value[0] === 'measurementUnitName') setMeasurementUnitName(value[1]);
          else if (value[0] === 'measurementUnitId') setMeasurementUnitId(value[1]);
        });
        setInitialValuesSet(true);
      }
    }, [isEdit, formik?.values, inputValues])

    const getFilterValues = (detailLevel: string) => {
      // Avoid fetching until we have a conversion source to filter by
      if (!defaultConversionSourceId && !conversionSourceId) {
        return;
      }
      if (!groupId && !selectedGroupId && disableFields) {
        return;
      }
      const gid = groupId || selectedGroupId;
      if (!isValidUuid(gid)) {
        return;
      }
      const catId = categoryId || selectedCategoryId;
      if (!isValidUuid(catId)) {
        return;
      }
      const groupIdForQuery = gid;
      const categoryIdForQuery = catId;
      const conversionIdForQuery = defaultConversionSourceId || conversionSourceId!
      if (detailLevel === 'detailLevelOne') {
        const queryString = buildCategoryFilterQueryString({
          calendarYear: calendarYear || 'null',
          groupId: groupIdForQuery,
          defaultConversionSourceId: conversionIdForQuery,
          selectedData: {},
        });
        const requestKey = `${categoryIdForQuery}${queryString}`
        latestDetailLevelOneRequestRef.current = requestKey
        categoryService.getOneCategoryFilter(categoryIdForQuery, queryString)
          .then(response => {
            if (latestDetailLevelOneRequestRef.current !== requestKey) return
            setDetailLevelOneFilters(response.detailLevelOne || [])
            setDetailLevelOneLoaded(true)
            if (response.measurementUnits) {
              setMeasurementUnitNameFilters(response.measurementUnits)
            }
          })
          .catch(err => console.log(err))
      }
      if (detailLevel === 'detailLevelTwo') {
        const queryString = buildCategoryFilterQueryString({
          calendarYear: calendarYear || 'null',
          groupId: groupIdForQuery,
          defaultConversionSourceId: conversionIdForQuery,
          selectedData: { detailLevelOne: detailLevelOne },
        });
        categoryService.getOneCategoryFilter(categoryIdForQuery, queryString)
          .then(response => {
            setDetailLevelTwoFilters(response.detailLevelTwo || [])
            if (response.measurementUnits) {
              setMeasurementUnitNameFilters(response.measurementUnits)
            }
          })
          .catch(err => {
            console.log(err)
          })
      }
      if (detailLevel === 'detailLevelThree') {
        const queryString = buildCategoryFilterQueryString({
          calendarYear: calendarYear || 'null',
          groupId: groupIdForQuery,
          defaultConversionSourceId: conversionIdForQuery,
          selectedData: { detailLevelOne: detailLevelOne, detailLevelTwo: detailLevelTwo },
        });
        categoryService.getOneCategoryFilter(categoryIdForQuery, queryString)
          .then(response => {
            setDetailLevelThreeFilters(response.detailLevelThree || [])
            if (response.measurementUnits) {
              setMeasurementUnitNameFilters(response.measurementUnits)
            }
          })
          .catch(err => console.log(err))
      }
      if (detailLevel === 'detailLevelFour') {
        const queryString = buildCategoryFilterQueryString({
          calendarYear: calendarYear || 'null',
          groupId: groupIdForQuery,
          defaultConversionSourceId: conversionIdForQuery,
          selectedData: { detailLevelOne: detailLevelOne, detailLevelTwo: detailLevelTwo, detailLevelThree: detailLevelThree },
        });
        categoryService.getOneCategoryFilter(categoryIdForQuery, queryString)
          .then(response => {
            setDetailLevelFourFilters(response.detailLevelFour || [])
            if (response.measurementUnits) {
              setMeasurementUnitNameFilters(response.measurementUnits)
            }
          })
          .catch(err => console.log(err))
      }
      if (detailLevel === 'detailLevelFive') {
        const queryString = buildCategoryFilterQueryString({
          calendarYear: calendarYear || 'null',
          groupId: groupIdForQuery,
          defaultConversionSourceId: conversionIdForQuery,
          selectedData: { detailLevelOne: detailLevelOne, detailLevelTwo: detailLevelTwo, detailLevelThree: detailLevelThree, detailLevelFour: detailLevelFour },
        });
        categoryService.getOneCategoryFilter(categoryIdForQuery, queryString)
          .then(response => {
            setDetailLevelFiveFilters(response.detailLevelFive || [])
            if (response.measurementUnits) {
              setMeasurementUnitNameFilters(response.measurementUnits)
            }
          })
          .catch(err => console.log(err))
      }
      if (detailLevel === 'measurementUnitName') {
        const queryString = buildCategoryFilterQueryString({
          calendarYear: calendarYear || 'null',
          groupId: groupIdForQuery,
          defaultConversionSourceId: conversionIdForQuery,
          selectedData: { detailLevelOne: detailLevelOne, detailLevelTwo: detailLevelTwo, detailLevelThree: detailLevelThree, detailLevelFour: detailLevelFour, detailLevelFive: detailLevelFive },
        });
        categoryService.getOneCategoryFilter(categoryIdForQuery, queryString)
          .then(response => {
            if (response.measurementUnits) {
              setMeasurementUnitNameFilters(response.measurementUnits || [])
            }
          })
          .catch(err => console.log(err))
      }
    }

    useEffect(() => {
      if (!groupId && !selectedGroupId && disableFields) {
        return;
      }
      const gid = groupId || selectedGroupId;
      const catResolved = categoryId || selectedCategoryId;
      const convResolved = defaultConversionSourceId || conversionSourceId;
      if (!isValidUuid(gid) || !isValidUuid(catResolved) || !convResolved) {
        return;
      }
      const hasCategory = isValidUuid(catResolved);
      const hasConversion = !!convResolved;
      if (hasCategory && hasConversion) {
        if (!detailLevelOne) {
          const queryString = buildCategoryFilterQueryString({
            calendarYear: calendarYear || 'null',
            groupId: gid,
            defaultConversionSourceId: convResolved,
            selectedData: {},
          });
          const requestKey = `${catResolved}${queryString}`
          latestDetailLevelOneRequestRef.current = requestKey
          categoryService.getOneCategoryFilter(catResolved, queryString)
            .then(response => {
              if (latestDetailLevelOneRequestRef.current !== requestKey) return
              setDetailLevelOneFilters(response.detailLevelOne || [])
              setDetailLevelOneLoaded(true)
              if (response.measurementUnits) {
                setMeasurementUnitNameFilters(response.measurementUnits)
              }
            })
            .catch(err => console.log(err))
        }
        if (!detailLevelTwo) {
          const queryString = buildCategoryFilterQueryString({
            calendarYear: calendarYear || 'null',
            groupId: gid,
            defaultConversionSourceId: convResolved,
            selectedData: { detailLevelOne: detailLevelOne },
          });
          categoryService.getOneCategoryFilter(catResolved, queryString)
            .then(response => {
              setDetailLevelTwoFilters(response.detailLevelTwo || [])
              if (response.measurementUnits) {
                setMeasurementUnitNameFilters(response.measurementUnits)
              }
            })
            .catch(err => console.log(err))
        }
        if (!detailLevelThree) {
          const queryString = buildCategoryFilterQueryString({
            calendarYear: calendarYear || 'null',
            groupId: gid,
            defaultConversionSourceId: convResolved,
            selectedData: { detailLevelOne: detailLevelOne, detailLevelTwo: detailLevelTwo, detailLevelThree: detailLevelThree, detailLevelFour: detailLevelFour },
          });
          categoryService.getOneCategoryFilter(catResolved, queryString)
            .then(response => {
              setDetailLevelThreeFilters(response.detailLevelThree || [])
              setDetailLevelFiveFilters(response.detailLevelFive || [])
              if (response.measurementUnits) {
                setMeasurementUnitNameFilters(response.measurementUnits)
              }
            })
            .catch(err => console.log(err))
        }
        if (!detailLevelFour) {
          const queryString = buildCategoryFilterQueryString({
            calendarYear: calendarYear || 'null',
            groupId: gid,
            defaultConversionSourceId: convResolved,
            selectedData: { detailLevelOne: detailLevelOne, detailLevelTwo: detailLevelTwo, detailLevelThree: detailLevelThree, detailLevelFour: detailLevelFour },
          });
          categoryService.getOneCategoryFilter(catResolved, queryString)
            .then(response => {
              setDetailLevelFourFilters(response.detailLevelFour || [])
              setDetailLevelFiveFilters(response.detailLevelFive || [])
              if (response.measurementUnits) {
                setMeasurementUnitNameFilters(response.measurementUnits)
              }
            })
            .catch(err => console.log(err))
        }
        if (!detailLevelFive) {
          const queryString = buildCategoryFilterQueryString({
            calendarYear: calendarYear || 'null',
            groupId: gid,
            defaultConversionSourceId: convResolved,
            selectedData: { detailLevelOne: detailLevelOne, detailLevelTwo: detailLevelTwo, detailLevelThree: detailLevelThree, detailLevelFour: detailLevelFour },
          });
          categoryService.getOneCategoryFilter(catResolved, queryString)
            .then(response => {
              setDetailLevelFiveFilters(response.detailLevelFive || [])
              if (response.measurementUnits) {
                setMeasurementUnitNameFilters(response.measurementUnits)
              }
            })
            .catch(err => console.log(err))
        }
        if (!measurementUnitName) {
          const queryString = buildCategoryFilterQueryString({
            calendarYear: calendarYear || 'null',
            groupId: gid,
            defaultConversionSourceId: convResolved,
            selectedData: { detailLevelOne: detailLevelOne, detailLevelTwo: detailLevelTwo, detailLevelThree: detailLevelThree, detailLevelFour: detailLevelFour, detailLevelFive: detailLevelFive },
          });
          categoryService.getOneCategoryFilter(catResolved, queryString)
            .then(response => {
              if (response.measurementUnits) {
                setMeasurementUnitNameFilters(response.measurementUnits || [])
              }
            })
            .catch(err => console.log(err))
        }
        if (measurementUnitName) {
          const queryString = buildCategoryFilterQueryString({
            calendarYear: calendarYear || 'null',
            groupId: gid,
            defaultConversionSourceId: convResolved,
            selectedData: { detailLevelOne: detailLevelOne, detailLevelTwo: detailLevelTwo, detailLevelThree: detailLevelThree, detailLevelFour: detailLevelFour, detailLevelFive: detailLevelFive, measurementUnitId: measurementUnitId },
          });
          // Two quick consecutive selections (easy with search picks) fire two of these
          // derivations; only the latest may write returnId, or the slower response
          // would overwrite the newer selection's id with the older one's.
          const requestKey = `${catResolved}${queryString}`
          latestReturnIdRequestRef.current = requestKey
          categoryService.getOneCategoryFilter(catResolved, queryString)
            .then(response => {
              if (latestReturnIdRequestRef.current !== requestKey) return
              if (response.returnId) {
                setReturnId(response.returnId)
                setSelectionUnavailableForYear(false)
                if (formik) {
                  formik.setFieldValue('returnId', response.returnId)
                }
              } else {
                setReturnId(undefined)
                // A complete selection that resolves to nothing means this activity is not
                // published for this factor year. Say so: the field still shows the activity,
                // so without this the only signal is a disabled Save telling the user to
                // finish a choice they have already made.
                setSelectionUnavailableForYear(true)
                if (formik) {
                  formik.setFieldValue('returnId', '')
                }
              }
            })
            .catch(err => console.log(err))
        }
      }
    }, [detailLevelOne, detailLevelTwo, detailLevelThree, detailLevelFour, detailLevelFive, measurementUnitName, calendarYear])

    const handleValueSelection = (detailLevel: string, value: string) => {
      // A manual cascade choice supersedes any search pick, so auto-fill may resume.
      searchSelectionActiveRef.current = false;
      factorClearedRef.current = false;
      if (detailLevel === 'detailLevelOne') {
        setDetailLevelOne(value);
        setDetailLevelTwo(undefined)
        setDetailLevelThree(undefined)
        setDetailLevelFour(undefined)
        setDetailLevelFive(undefined)
        setMeasurementUnitName(undefined)
        setMeasurementUnitId(undefined)
        setDetailLevelTwoFilters([])
        setDetailLevelThreeFilters([])
        setDetailLevelFourFilters([])
        setDetailLevelFiveFilters([])
        setMeasurementUnitNameFilters([])
        if (formik) {
          formik.setFieldValue('detailLevelOne', value)
          formik.setFieldValue('detailLevelTwo', '')
          formik.setFieldValue('detailLevelThree', '')
          formik.setFieldValue('detailLevelFour', '')
          formik.setFieldValue('detailLevelFive', '')
          formik.setFieldValue('measurementUnitName', '')
          formik.setFieldValue('measurementUnitId', '')
          formik.setFieldValue('returnId', '')
        }
      }
      if (detailLevel === 'detailLevelTwo') {
        setDetailLevelTwo(value);
        setDetailLevelThree(undefined)
        setDetailLevelFour(undefined)
        setDetailLevelFive(undefined)
        setMeasurementUnitName(undefined)
        setMeasurementUnitId(undefined)
        setDetailLevelThreeFilters([])
        setDetailLevelFourFilters([])
        setDetailLevelFiveFilters([])
        setMeasurementUnitNameFilters([])
        if (formik) {
          formik.setFieldValue('detailLevelTwo', value)
          formik.setFieldValue('detailLevelThree', '')
          formik.setFieldValue('detailLevelFour', '')
          formik.setFieldValue('detailLevelFive', '')
          formik.setFieldValue('measurementUnitName', '')
          formik.setFieldValue('measurementUnitId', '')
          formik.setFieldValue('returnId', '')
        }
      }
      if (detailLevel === 'detailLevelThree') {
        setDetailLevelThree(value)
        setDetailLevelFour(undefined)
        setDetailLevelFive(undefined)
        setMeasurementUnitName(undefined)
        setMeasurementUnitId(undefined)
        setDetailLevelFourFilters([])
        setDetailLevelFiveFilters([])
        setMeasurementUnitNameFilters([])
        if (formik) {
          formik.setFieldValue('detailLevelThree', value)
          formik.setFieldValue('detailLevelFour', '')
          formik.setFieldValue('detailLevelFive', '')
          formik.setFieldValue('measurementUnitName', '')
          formik.setFieldValue('measurementUnitId', '')
          formik.setFieldValue('returnId', '')
        }
      }
      if (detailLevel === 'detailLevelFour') {
        setDetailLevelFour(value)
        setDetailLevelFive(undefined)
        setMeasurementUnitName(undefined)
        setMeasurementUnitId(undefined)
        setDetailLevelFiveFilters([])
        setMeasurementUnitNameFilters([])
        if (formik) {
          formik.setFieldValue('detailLevelFour', value)
          formik.setFieldValue('detailLevelFive', '')
          formik.setFieldValue('measurementUnitName', '')
          formik.setFieldValue('measurementUnitId', '')
          formik.setFieldValue('returnId', '')
        }
      }
      if (detailLevel === 'detailLevelFive') {
        setDetailLevelFive(value)
        setMeasurementUnitName(undefined)
        setMeasurementUnitId(undefined)
        setMeasurementUnitNameFilters([])
        if (formik) {
          formik.setFieldValue('detailLevelFive', value)
          formik.setFieldValue('measurementUnitName', '')
          formik.setFieldValue('measurementUnitId', '')
          formik.setFieldValue('returnId', '')
        }
      }
      if (detailLevel === 'measurementUnitName') {
        setMeasurementUnitName(value);
        setMeasurementUnitId(measurementUnitNameFilters.find(unit => unit.name === value)?.id)
        if (formik) {
          formik.setFieldValue('measurementUnitName', value)
          formik.setFieldValue('measurementUnitId', measurementUnitNameFilters.find(unit => unit.name === value)?.id)
        }
      }
    }

    // One auto-pick per render pass, top level first; each pick re-runs the effect via its
    // state deps, so a single-option chain fills level by level through the same
    // handleValueSelection cascade a user click takes. A level with several options (or a
    // still-loading fetch, whose list is empty) stops the chain and waits for the user.
    useEffect(() => {
      if (!autoSelectSingleOptions || isEdit) return
      // A chosen unit means the selection is already complete: never auto-fill under it.
      // Factor paths are routinely ragged in depth (partner custom sources mix two- and
      // three-level entries under one category), so after a search pick of a SHALLOWER
      // path the option fetches for its empty deeper levels can come back with exactly
      // one distinct sibling value; auto-picking it would wipe the unit and returnId,
      // re-walk the cascade and silently seed a different, saveable conversion. The
      // search-selection flag covers the same race for a pick whose unit has not
      // landed in state yet.
      if (measurementUnitName || searchSelectionActiveRef.current || factorClearedRef.current) return
      if (!detailLevelOne && detailLevelOneFilters.length === 1) { handleValueSelection('detailLevelOne', detailLevelOneFilters[0]); return }
      if (detailLevelOne && !detailLevelTwo && detailLevelTwoFilters.length === 1) { handleValueSelection('detailLevelTwo', detailLevelTwoFilters[0]); return }
      if (detailLevelTwo && !detailLevelThree && detailLevelThreeFilters.length === 1) { handleValueSelection('detailLevelThree', detailLevelThreeFilters[0]); return }
      if (detailLevelThree && !detailLevelFour && detailLevelFourFilters.length === 1) { handleValueSelection('detailLevelFour', detailLevelFourFilters[0]); return }
      if (detailLevelFour && !detailLevelFive && detailLevelFiveFilters.length === 1) { handleValueSelection('detailLevelFive', detailLevelFiveFilters[0]); return }
      if (!measurementUnitName && measurementUnitNameFilters.length === 1) { handleValueSelection('measurementUnitName', measurementUnitNameFilters[0].name) }
    }, [autoSelectSingleOptions, isEdit, detailLevelOne, detailLevelTwo, detailLevelThree, detailLevelFour, detailLevelFive, measurementUnitName, detailLevelOneFilters, detailLevelTwoFilters, detailLevelThreeFilters, detailLevelFourFilters, detailLevelFiveFilters, measurementUnitNameFilters]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (onValueChange && initialValuesSet && (categoryId || selectedCategoryId) && (groupId || selectedGroupId) && (defaultConversionSourceId || conversionSourceId)) {
        onValueChange({
          detailLevelOne: detailLevelOne || '',
          detailLevelTwo: detailLevelTwo || '',
          detailLevelThree: detailLevelThree || '',
          detailLevelFour: detailLevelFour || '',
          detailLevelFive: detailLevelFive || '',
          measurementUnitName: measurementUnitName || '',
          returnId: returnId
        })
      }
    }, [detailLevelOne, detailLevelTwo, detailLevelTwo, detailLevelThree, detailLevelFour, detailLevelFive, measurementUnitName, returnId])

    const conversionSourceHandler = (e: SelectChangeEvent) => {
      setConversionSourceId(e.target.value)
      // The source now sits below the group and category, so wiping them here would throw away
      // the two answers the user just gave to change the one underneath. A different source is a
      // different set of factors for the SAME activity, so only the chosen factor is invalidated;
      // where the new source publishes nothing for this category, the empty state says so.
      clearFactorSelection()
      setSelectedFactorOption(null)
      if (formik) formik.setFieldValue('conversionSourceId', e.target.value)
    }

    const selectGroupHandler = (value: string) => {
      const newGroup = groups?.find(group => group.id === value)

      setSelectedGroupId(newGroup?.id)
      setCategories(newGroup?.categories)
      setSelectedCategoryId('')
      setSelectedCategoryName(undefined)

      // Set groupId in formik if provided
      if (formik && newGroup?.id) {
        formik.setFieldValue('groupId', newGroup.id);
      }
    }

    const selectCategoryHandler = (value: string) => {
      setSelectedCategoryName(value)
      const cat = findCategoryByDisplayName(categories, value);
      setSelectedCategoryId(cat?.id);
      if (formik && cat?.id) {
        formik.setFieldValue('categoryId', cat.id);
      }
    }

    useEffect(() => {
      if (!groupId && !selectedGroupId && disableFields) {
        return;
      }
      getFilterValues('detailLevelOne')
    }, [selectedCategoryId])

    // Any change to the query identity invalidates the loaded flag: the empty-state
    // message must never describe a combination whose options were not fetched.
    useEffect(() => {
      setDetailLevelOneLoaded(false)
    }, [categoryId, groupId, defaultConversionSourceId, calendarYear, selectedCategoryId, selectedGroupId, conversionSourceId])

    // When a detail level value is present (set by seeding or by user interaction),
    // load the option list for the next level so the dropdown isn't empty.
    useEffect(() => {
      if (detailLevelOne) getFilterValues('detailLevelTwo')
    }, [detailLevelOne]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (detailLevelTwo) getFilterValues('detailLevelThree')
    }, [detailLevelTwo]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (detailLevelThree) getFilterValues('detailLevelFour')
    }, [detailLevelThree]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (detailLevelFour) getFilterValues('detailLevelFive')
    }, [detailLevelFour]) // eslint-disable-line react-hooks/exhaustive-deps

    const canRenderDetails = ((selectedCategoryId || categoryId)
      && (((conversionSourceId || defaultConversionSourceId) != null) || (isEdit && (detailLevelOne || detailLevelTwo || detailLevelThree || detailLevelFour || detailLevelFive || measurementUnitName)))
      && ((selectedGroupId || groupId) || !allowGroupSelect));

    const showLevelOneSelect = Boolean(detailLevelOne || detailLevelOneFilters.length > 0);
    const showUnitSelect = Boolean(measurementUnitName || measurementUnitNameFilters.length > 0);

    // ── Search-first mode logic ──
    const effectiveGroupId = groupId || selectedGroupId;
    const effectiveCategoryId = categoryId || selectedCategoryId;
    const effectiveSourceId = defaultConversionSourceId || conversionSourceId;
    // Search needs the same identity the cascade fetches need. disableFields means the
    // selection is fixed by the caller, so there is nothing to search for.
    const searchAvailable = enableSearch && !disableFields
      && isValidUuid(effectiveGroupId) && isValidUuid(effectiveCategoryId) && Boolean(effectiveSourceId);

    // Flattened search on the debounced query once it is long enough; the stale-response
    // guard keeps a slower earlier response from overwriting a newer query's results.
    const debouncedFactorSearchQuery = useDebounce(factorSearchInput.trim(), FACTOR_SEARCH_DEBOUNCE_MS);
    useEffect(() => {
      if (!searchAvailable) return;
      const query = debouncedFactorSearchQuery;
      // An empty box browses the category rather than sitting idle: results are one row per
      // activity now, so most categories are a short list (three for Purchased Energy) and the
      // endpoint already answers a blank query. Only a part-typed word is worth waiting on,
      // because it would return a near-arbitrary slice of the category.
      if (query.length > 0 && query.length < FACTOR_SEARCH_MIN_CHARS) {
        setFactorSearchResults([]);
        setFactorSearchHasMore(false);
        setFactorSearchLoading(false);
        return;
      }
      const requestKey = `${effectiveCategoryId}|${effectiveGroupId}|${effectiveSourceId}|${calendarYear || 'null'}|${query}`;
      latestFactorSearchRef.current = requestKey;
      setFactorSearchLoading(true);
      const queryString = buildCategoryFilterQueryString({
        calendarYear: calendarYear || 'null',
        groupId: effectiveGroupId!,
        defaultConversionSourceId: effectiveSourceId!,
        selectedData: { q: query },
      });
      categoryService.searchCategoryFactors(effectiveCategoryId!, queryString)
        .then(response => {
          if (latestFactorSearchRef.current !== requestKey) return;
          setFactorSearchResults(response.results || []);
          setFactorSearchHasMore(Boolean(response.hasMore));
        })
        .catch(err => console.log(err))
        .finally(() => {
          if (latestFactorSearchRef.current === requestKey) setFactorSearchLoading(false);
        });
    }, [debouncedFactorSearchQuery, searchAvailable, effectiveCategoryId, effectiveGroupId, effectiveSourceId, calendarYear]); // eslint-disable-line react-hooks/exhaustive-deps

    // A different source, category or group is a genuinely different search space: nothing
    // the user chose in the old one still describes this one.
    useEffect(() => {
      setFactorSearchResults([]);
      setFactorSearchHasMore(false);
      setSelectedFactorOption(null);
      setFactorSearchInput('');
      setRecentFactorUnavailable(undefined);
      setSelectionUnavailableForYear(false);
      // The old search selection no longer describes this space, so auto-fill may resume
      // and a pre-seeded edit selection may present its summary again.
      searchSelectionActiveRef.current = false;
      setChangeSelectionRequested(false);
    }, [effectiveCategoryId, effectiveGroupId, effectiveSourceId]);

    // A new factor year is the SAME activity in another vintage, not another search space, so
    // only the cached results are stale. The chosen activity carries over and the resolution
    // effect re-resolves it onto the new year through the identity the server matches on.
    //
    // This used to clear the displayed selection too, while leaving the detail levels, unit and
    // returnId untouched. Changing the start date into another year therefore blanked the
    // Emission factor field while the record still held a factor, leaving an empty field above
    // an enabled Save.
    useEffect(() => {
      setFactorSearchResults([]);
      setFactorSearchHasMore(false);
      setSelectionUnavailableForYear(false);
    }, [calendarYear]);

    const factorResultOption = (result: IFactorSearchResult): IFactorSearchOption => {
      const pathLabel = factorPathText([result.detailLevelOne, result.detailLevelTwo, result.detailLevelThree, result.detailLevelFour, result.detailLevelFive]);
      // The unit is chosen beside the amount now, so it is context here rather than part
      // of the identity: one row per activity, with the units it can be recorded in.
      const units = factorResultUnits(result);
      const unitLabel = units.length === 1 ? `per ${units[0].name}` : units.map(unit => unit.name).join(', ');
      return {
        key: result.id,
        group: 'Matches',
        pathLabel,
        metaLabel: [unitLabel, result.scope ? `Scope ${result.scope}` : ''].filter(Boolean).join(' · '),
        label: pathLabel,
        result,
      };
    };

    const recentFactorOptions = useMemo<IFactorSearchOption[]>(() => {
      if (!searchAvailable) return [];
      return readRecentFactors(organisationId)
        .filter(recent => recent.conversionSourceId === effectiveSourceId
          && recent.categoryId === effectiveCategoryId
          && recent.groupId === effectiveGroupId)
        .slice(0, RECENT_FACTORS_SHOWN)
        .map(recent => {
          const pathLabel = factorPathText([recent.detailLevelOne, recent.detailLevelTwo, recent.detailLevelThree, recent.detailLevelFour, recent.detailLevelFive]);
          return {
            key: `recent|${recentFactorKey(recent)}`,
            group: 'Recently used' as const,
            pathLabel,
            metaLabel: [recent.measurementUnitName ? `per ${recent.measurementUnitName}` : '', recent.scope ? `Scope ${recent.scope}` : ''].filter(Boolean).join(' · '),
            label: `${pathLabel}${recent.measurementUnitName ? ` (${recent.measurementUnitName})` : ''}`,
            recent,
          };
        });
      // recentsVersion is the localStorage invalidation signal: a new pick bumps it so
      // the list re-reads storage, which the linter cannot see as a dependency.
    }, [searchAvailable, organisationId, effectiveSourceId, effectiveCategoryId, effectiveGroupId, recentsVersion]); // eslint-disable-line react-hooks/exhaustive-deps

    const trimmedFactorSearchInput = factorSearchInput.trim();
    // Nothing typed: the list is the category's own factors, with anything recently used
    // promoted above them. Part-typed: still recents only, since no query has run.
    const browsingCategory = trimmedFactorSearchInput.length === 0;
    const showingRecents = trimmedFactorSearchInput.length < FACTOR_SEARCH_MIN_CHARS;
    // The request only fires once the debounce settles, so factorSearchLoading is still false
    // for the first 400ms of typing. Reporting that window as "not searching" let the
    // Autocomplete show "No matching factors" over a search that had not started yet. Treat
    // an input the debounce has not caught up with as searching.
    const factorSearchPending = (!showingRecents || browsingCategory)
      && (factorSearchLoading || trimmedFactorSearchInput !== debouncedFactorSearchQuery);
    const searchResultOptions = factorSearchResults.map(factorResultOption);
    const baseFactorOptions = browsingCategory
      ? [...recentFactorOptions, ...searchResultOptions.filter(option => !recentFactorOptions.some(
        recent => normaliseFactorText(recent.pathLabel) === normaliseFactorText(option.pathLabel)))]
      : showingRecents ? recentFactorOptions : searchResultOptions;
    // A selection carried across a factor year is the old year's row, so the new year's search
    // returns the same activity under a different id. Show the fresh one rather than listing the
    // activity twice; the identity, which is what actually resolves, is the same either way.
    const selectedPathKey = selectedFactorOption ? normaliseFactorText(selectedFactorOption.pathLabel) : null;
    const freshEquivalentOption = selectedPathKey
      ? baseFactorOptions.find(option => normaliseFactorText(option.pathLabel) === selectedPathKey)
      : undefined;
    const displayedFactorOption = freshEquivalentOption ?? selectedFactorOption;
    // Keep the chosen option present so the Autocomplete can display it as its value, but do
    // not let it sit under "Matches" while a search is running: typing a word this factor does
    // not contain and being shown it as a match reads as the search ignoring the query.
    const retainedSelectionOption = displayedFactorOption
      && !baseFactorOptions.some(option => option.key === displayedFactorOption.key)
      ? { ...displayedFactorOption, group: 'Current selection' as const }
      : undefined;
    const factorOptions = retainedSelectionOption
      ? [retainedSelectionOption, ...baseFactorOptions]
      : baseFactorOptions;
    // A finished search that matched nothing. The retained selection above keeps the option
    // list non-empty, so the Autocomplete's own no-options text never gets the chance to say
    // this, and the search is scoped to one category: nothing outside it can ever match.
    const searchMatchedNothing = !browsingCategory && !showingRecents && !factorSearchPending
      && searchResultOptions.length === 0;
    // Said in both places because they are visible at different moments: the popup covers the
    // helper text while it is open, and closes over it when it is not. A search never leaves
    // its category, which is the part people do not expect, so it leads.
    const noMatchesText = isEdit
      ? 'No matching factors in this record\u2019s category. A record keeps the category it was created in, so a factor from another category means adding a new record instead.'
      : 'No matching factors in this category. Try different words, or go back and choose a different category.';

    // Emptying the field has to empty the record behind it. The Autocomplete only owned the
    // displayed option, so a cleared field still held its levels, its unit and its returnId: the
    // box looked empty, Save stayed armed, and saving wrote back the factor the user had just
    // removed. Clear what a selection actually consists of.
    const clearFactorSelection = () => {
      factorClearedRef.current = true;
      searchSelectionActiveRef.current = false;
      setDetailLevelOne(undefined);
      setDetailLevelTwo(undefined);
      setDetailLevelThree(undefined);
      setDetailLevelFour(undefined);
      setDetailLevelFive(undefined);
      setMeasurementUnitName(undefined);
      setMeasurementUnitId(undefined);
      setReturnId(undefined);
      setSelectionUnavailableForYear(false);
      // Level one's options belong to the category, not to the choice, so they stay: the cascade
      // underneath has to keep working. Everything below level one described the cleared choice.
      setDetailLevelTwoFilters([]);
      setDetailLevelThreeFilters([]);
      setDetailLevelFourFilters([]);
      setDetailLevelFiveFilters([]);
      setMeasurementUnitNameFilters([]);
      if (formik) {
        formik.setFieldValue('detailLevelOne', '');
        formik.setFieldValue('detailLevelTwo', '');
        formik.setFieldValue('detailLevelThree', '');
        formik.setFieldValue('detailLevelFour', '');
        formik.setFieldValue('detailLevelFive', '');
        formik.setFieldValue('measurementUnitName', '');
        formik.setFieldValue('measurementUnitId', '');
        formik.setFieldValue('returnId', '');
      }
    };

    // Seed everything the cascade's final step would have written, in one action.
    // The existing resolution effect then re-derives returnId from the same filter
    // endpoint the cascade uses, so search and cascade cannot disagree on the saved id.
    const applyFactorSearchSelection = (result: IFactorSearchResult, preferredUnitName?: string | null) => {
      // A pick settles the unit only when there is nothing to choose, or when a recent
      // remembers which one was used last time. Otherwise the unit stays empty and the
      // select beside the amount asks for it, which is the point of the split.
      const units = factorResultUnits(result);
      const preferred = preferredUnitName
        ? units.find(candidate => normaliseFactorText(candidate.name) === normaliseFactorText(preferredUnitName))
        : undefined;
      const unit = preferred ?? (units.length === 1 ? units[0] : null);
      // Without a unit there is no single conversion yet; the resolution effect fills
      // returnId in as soon as one is chosen.
      const resolvedReturnId = unit ? (unit.conversionId ?? result.id) : '';
      // Block the single-option auto-fill until the user re-walks the cascade or the
      // query identity changes, so the pick cannot be trampled (see the auto-fill guard).
      searchSelectionActiveRef.current = true;
      factorClearedRef.current = false;
      setDetailLevelOne(result.detailLevelOne ?? '');
      setDetailLevelTwo(result.detailLevelTwo ?? '');
      setDetailLevelThree(result.detailLevelThree ?? '');
      setDetailLevelFour(result.detailLevelFour ?? '');
      setDetailLevelFive(result.detailLevelFive ?? '');
      setMeasurementUnitName(unit?.name ?? '');
      setMeasurementUnitId(unit?.id ?? undefined);
      // Seed the options from the pick itself. The resolution effect fetches the same list
      // a moment later, but waiting for it would leave the unit select missing in the gap
      // right after choosing an activity, which is exactly when it is being looked for.
      if (units.length > 0) {
        setMeasurementUnitNameFilters(units.map(candidate => ({ id: candidate.id, name: candidate.name })));
      }
      setReturnId(resolvedReturnId || undefined);
      if (formik) {
        formik.setFieldValue('detailLevelOne', result.detailLevelOne ?? '');
        formik.setFieldValue('detailLevelTwo', result.detailLevelTwo ?? '');
        formik.setFieldValue('detailLevelThree', result.detailLevelThree ?? '');
        formik.setFieldValue('detailLevelFour', result.detailLevelFour ?? '');
        formik.setFieldValue('detailLevelFive', result.detailLevelFive ?? '');
        formik.setFieldValue('measurementUnitName', unit?.name ?? '');
        formik.setFieldValue('measurementUnitId', unit?.id ?? '');
        formik.setFieldValue('returnId', resolvedReturnId);
      }
      if (effectiveSourceId && effectiveCategoryId && effectiveGroupId) {
        storeRecentFactor(organisationId, {
          detailLevelOne: result.detailLevelOne ?? '',
          detailLevelTwo: result.detailLevelTwo ?? '',
          detailLevelThree: result.detailLevelThree ?? '',
          detailLevelFour: result.detailLevelFour ?? '',
          detailLevelFive: result.detailLevelFive ?? '',
          measurementUnitName: unit?.name ?? '',
          scope: result.scope ?? null,
          conversionSourceId: effectiveSourceId,
          categoryId: effectiveCategoryId,
          groupId: effectiveGroupId,
          savedAt: Date.now(),
        });
        setRecentsVersion(version => version + 1);
      }
    };

    // A recent pick is re-resolved against the current year and source before seeding:
    // conversion ids are year-specific, so the stored path is looked up fresh.
    const applyRecentFactorSelection = (recent: IRecentFactorSelection) => {
      if (!effectiveCategoryId || !effectiveGroupId || !effectiveSourceId) return;
      const query = [recent.detailLevelOne, recent.detailLevelTwo, recent.detailLevelThree, recent.detailLevelFour, recent.detailLevelFive, recent.measurementUnitName]
        .filter(value => Boolean(value && value.trim()))
        .join(' ');
      const queryString = buildCategoryFilterQueryString({
        calendarYear: calendarYear || 'null',
        groupId: effectiveGroupId,
        defaultConversionSourceId: effectiveSourceId,
        selectedData: { q: query },
      });
      // Only the latest pick may seed anything: a slower earlier resolution must not
      // overwrite what the user chose after it.
      const requestKey = `${recentFactorKey(recent)}|${calendarYear || 'null'}`;
      latestRecentResolveRef.current = requestKey;
      setFactorSearchLoading(true);
      categoryService.searchCategoryFactors(effectiveCategoryId, queryString)
        .then(response => {
          if (latestRecentResolveRef.current !== requestKey) return;
          const match = (response.results || []).find(result =>
            normaliseFactorText(result.detailLevelOne) === normaliseFactorText(recent.detailLevelOne)
            && normaliseFactorText(result.detailLevelTwo) === normaliseFactorText(recent.detailLevelTwo)
            && normaliseFactorText(result.detailLevelThree) === normaliseFactorText(recent.detailLevelThree)
            && normaliseFactorText(result.detailLevelFour) === normaliseFactorText(recent.detailLevelFour)
            && normaliseFactorText(result.detailLevelFive) === normaliseFactorText(recent.detailLevelFive));
          if (match) {
            // A result is an activity now, so the remembered unit is restored on top of it
            // rather than being part of what identifies the match.
            applyFactorSearchSelection(match, recent.measurementUnitName);
            setSelectedFactorOption(factorResultOption(match));
          } else {
            setSelectedFactorOption(null);
            setRecentFactorUnavailable('That factor is not available for this source and year. Search for it instead.');
          }
        })
        .catch(err => {
          console.log(err);
          if (latestRecentResolveRef.current !== requestKey) return;
          setSelectedFactorOption(null);
          setRecentFactorUnavailable('That factor could not be looked up. Check your connection and try again.');
        })
        .finally(() => {
          if (latestRecentResolveRef.current === requestKey) setFactorSearchLoading(false);
        });
    };

    const searchSpaceKey = `${effectiveCategoryId || ''}|${effectiveGroupId || ''}|${effectiveSourceId || ''}|${calendarYear || ''}`;

    // The cascade already fills in a level that has only one answer. A category offering only
    // one activity is the same situation one step up, so the field fills itself rather than
    // asking the user to pick from a list of one. Only while browsing: mid-search a single
    // remaining match is a coincidence of what has been typed so far, not a settled choice.
    useEffect(() => {
      if (!autoSelectSingleOptions || !searchAvailable || !browsingCategory) return;
      if (factorSearchResults.length !== 1) return;
      if (selectedFactorOption) return;
      // Once per search space, and no more. Clearing the field is how you start choosing a
      // different factor, and in a category with a single activity that leaves exactly the
      // state this effect fires on, so without this it refilled the box faster than you could
      // type and editing became impossible.
      if (autoSelectedSpaceRef.current === searchSpaceKey) return;
      const onlyOption = factorSearchResults[0];
      // The cascade fills a single-answer level on its own, so by the time these results land
      // the levels often already hold this very activity. That is not a reason to stay silent:
      // the field should show what the record is set to, which is the whole complaint. It IS a
      // reason to keep out of the way when the levels hold something else.
      const levelsAreEmpty = !detailLevelOne && !detailLevelTwo && !detailLevelThree
        && !detailLevelFour && !detailLevelFive;
      const levelsMatchOnlyOption = ([
        [onlyOption.detailLevelOne, detailLevelOne],
        [onlyOption.detailLevelTwo, detailLevelTwo],
        [onlyOption.detailLevelThree, detailLevelThree],
        [onlyOption.detailLevelFour, detailLevelFour],
        [onlyOption.detailLevelFive, detailLevelFive],
      ] as Array<[string | null | undefined, string | null | undefined]>)
        .every(([fromResult, current]) => normaliseFactorText(fromResult) === normaliseFactorText(current));
      if (!levelsAreEmpty && !levelsMatchOnlyOption) return;
      autoSelectedSpaceRef.current = searchSpaceKey;
      // Seed the record only when there is nothing there; otherwise just show what is.
      if (levelsAreEmpty) applyFactorSearchSelection(onlyOption);
      setSelectedFactorOption(factorResultOption(onlyOption));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [factorSearchResults, browsingCategory, autoSelectSingleOptions, searchAvailable,
      detailLevelOne, detailLevelTwo, detailLevelThree, detailLevelFour, detailLevelFive]);

    // Browsing the levels underneath invalidates the displayed search selection.
    useEffect(() => {
      if (!selectedFactorOption?.result) return;
      const result = selectedFactorOption.result;
      const stillCurrent = normaliseFactorText(result.detailLevelOne) === normaliseFactorText(detailLevelOne)
        && normaliseFactorText(result.detailLevelTwo) === normaliseFactorText(detailLevelTwo)
        && normaliseFactorText(result.detailLevelThree) === normaliseFactorText(detailLevelThree)
        && normaliseFactorText(result.detailLevelFour) === normaliseFactorText(detailLevelFour)
        && normaliseFactorText(result.detailLevelFive) === normaliseFactorText(detailLevelFive);
      // The unit is deliberately not compared: it is chosen after the activity now, so
      // picking one must not read as "you have browsed away from your search result".
      if (!stillCurrent) setSelectedFactorOption(null);
    }, [detailLevelOne, detailLevelTwo, detailLevelThree, detailLevelFour, detailLevelFive]); // eslint-disable-line react-hooks/exhaustive-deps

    // Both handlers are read through refs so the publishing effect below can depend on the
    // CONTENT of the choice alone. If it depended on the callbacks, a parent passing an
    // inline arrow would re-run it every render, and each run sets parent state: a loop.
    const unitHandlersRef = useRef({
      select: (unitName: string) => { handleValueSelection('measurementUnitName', unitName); },
      refresh: () => { getFilterValues('measurementUnitName'); },
    });
    unitHandlersRef.current = {
      select: (unitName: string) => { handleValueSelection('measurementUnitName', unitName); },
      refresh: () => { getFilterValues('measurementUnitName'); },
    };
    const onUnitChoiceChangeRef = useRef(onUnitChoiceChange);
    onUnitChoiceChangeRef.current = onUnitChoiceChange;

    const unitChoiceSignature = externalUnitSelect
      ? JSON.stringify({
        show: showUnitSelect,
        value: measurementUnitName ?? '',
        disabled: disableFields,
        options: (measurementUnitNameFilters || []).map((unit: any) => ({ id: unit?.id, name: unit?.name })),
      })
      : '';

    useEffect(() => {
      if (!externalUnitSelect) return;
      const parsed = unitChoiceSignature ? JSON.parse(unitChoiceSignature) : null;
      onUnitChoiceChangeRef.current?.(parsed?.show
        ? {
          options: parsed.options.filter((unit: { id?: string, name?: string }) => unit?.name),
          value: parsed.value,
          disabled: parsed.disabled,
          onChange: (unitName: string) => unitHandlersRef.current.select(unitName),
          onOpen: () => unitHandlersRef.current.refresh(),
        }
        : null);
      // Unmounting (a confirmed selection replaces this component, or the form closes)
      // must take the parent's copy of the select with it.
      return () => { onUnitChoiceChangeRef.current?.(null); };
    }, [unitChoiceSignature, externalUnitSelect]);

    const selectionSummaryText = (() => {
      const path = factorPathText([detailLevelOne, detailLevelTwo, detailLevelThree, detailLevelFour, detailLevelFive]);
      if (!path && !measurementUnitName) return '';
      return `${path}${measurementUnitName ? `${path ? ' ' : ''}(${measurementUnitName})` : ''}`;
    })();

    // A pre-seed only counts as a finished selection when it includes the unit. Some
    // pre-seeds are partial (the vehicle default conversion writes detail levels and
    // returnId but no measurement unit); presenting those as a summary would unmount
    // the Measurement Unit select and its validation message, leaving a dead Save
    // with no visible control to fix it.
    const hasPreseededSelection = isEdit && Boolean(selectionSummaryText) && Boolean(measurementUnitName);
    const showCurrentSelectionSummary = searchAvailable && showSelectionSummary && hasPreseededSelection && !changeSelectionRequested;

    // For an incomplete pre-seed, keep the level controls visible (as the cascade-only
    // rendering did) so the user can complete the selection.
    useEffect(() => {
      if (isEdit && Boolean(selectionSummaryText) && !measurementUnitName) {
        setBrowseLevelsOpen(true);
      }
    }, [isEdit, selectionSummaryText, measurementUnitName]);


    // The unit is the second half of choosing a conversion factor, so it has to be reachable
    // wherever the factor is. It used to live at the bottom of the cascade, which is fine while
    // the cascade IS the picker, but the search box collapses the cascade by default: an
    // activity carrying several units (natural gas has four) then left the unit set to nothing
    // with no visible control to set it, and a Save that could never succeed. Rendered under
    // the search box when searching, at the foot of the cascade when browsing is all there is.
    // A call site passing externalUnitSelect renders its own unit beside the Amount and opts out.
    const unitSelectControl = showUnitSelect && !externalUnitSelect ? (
      <FormControl fullWidth size={size} variant='outlined'>
        <InputLabel>Measurement Unit</InputLabel>
        <Select
          label={isInputForm ? null : 'Measurement Unit'}
          size={size}
          disabled={disableFields}
          onOpen={() => { getFilterValues('measurementUnitName') }}
          value={measurementUnitName ?? ''}
          sx={{
            color: 'text.primary',
          }}
          onChange={(e) => handleValueSelection('measurementUnitName', e.target.value as string)}>
          {measurementUnitNameFilters?.length && measurementUnitNameFilters?.length > 0 ? measurementUnitNameFilters?.map((filter, index) => <MenuItem key={index} value={filter.name}>{filter.name}</MenuItem>) : <MenuItem value={measurementUnitName}>{measurementUnitName}</MenuItem>}
        </Select>
        {formik?.touched.measurementUnitId && formik?.errors.measurementUnitId &&
          <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
            {formik.errors.measurementUnitId ? <div>{formik.errors.measurementUnitId as string}</div> : null}
          </FormHelperText>
        }
      </FormControl>
    ) : null;

    return (
      <>
        {/* Group, then category, then the source the factors come from. The source is the one
            field here that is a setting rather than a description of the activity, and its
            options never depended on the other two, so leading with it asked the user to answer
            the least meaningful question first. */}
        {groups && allowGroupSelect && (
          <FormControl fullWidth size={size} variant='outlined'>
            <InputLabel htmlFor='groupName'>Group</InputLabel>
            <Select
              id='groupName' label='Group' name='groupName'
              value={selectedGroupId ?? ''}
              error={formik?.touched.groupId && Boolean(formik?.errors.groupId)}
              onChange={(e) => selectGroupHandler(e.target.value)}
              sx={{ color: 'text.primary', }}>
              {sortGroupsByGhgCategory(groups).map((group, index) =>
                <MenuItem key={index} value={group.id}>{formatGroupLabel(group)}</MenuItem>
              )}
            </Select>
            {formik?.touched.groupId && formik?.errors.groupId &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                {formik.errors.groupId ? <div>{formik.errors.groupId as string}</div> : null}
              </FormHelperText>
            }
          </FormControl>)}
        {categories && categories.length > 0 && allowCategorySelect && ((selectedGroupId || groupId) || !allowGroupSelect) &&
          <FormControl fullWidth size={size} variant='outlined' sx={{ mt: 2, mb: 2 }}>
            <InputLabel htmlFor='categoryName'>Category</InputLabel>
            <Select
              id='categoryName' label='Category' name='categoryName'
              value={selectedCategoryName ?? ''}
              error={formik?.touched.categoryId && Boolean(formik?.errors.categoryId)}
              onChange={(e) => selectCategoryHandler(e.target.value)}
              sx={{ color: 'text.primary', }}>
              {categories.map((category, index) =>
                <MenuItem key={index} value={category.name}>{getCategoryDisplayName(category)}</MenuItem>
              )}
            </Select>
            {formik?.touched.categoryId && formik?.errors.categoryId &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                {formik.errors.categoryId ? <div>{formik.errors.categoryId as string}</div> : null}
              </FormHelperText>
            }
          </FormControl>}
        {conversionSources && allowConversionSourceSelect &&
          <FormControl fullWidth size={size} variant='outlined' sx={{ mt: 2 }}>
            <InputLabel htmlFor='conversionSourceId'>Conversion Factor Source</InputLabel>
            <Select
              id='conversionSourceId' label='Conversion Factor Source' name='conversionSourceId'
              // An undefined value on the first render leaves MUI treating this as uncontrolled,
              // after which it keeps its own state and ignores anything seeded into it later.
              value={conversionSourceId ?? ''}
              error={formik?.touched.conversionSourceId && Boolean(formik?.errors.conversionSourceId)}
              onChange={conversionSourceHandler}
              sx={{ color: 'text.primary', }}>
              {conversionSources.sort((a, b) => a.source.localeCompare(b.source)).map((conversionSource, index) =>
                <MenuItem key={index} value={conversionSource.id}>{conversionSource.source}</MenuItem>
              )}
            </Select>
            {formik?.touched.conversionSourceId && formik?.errors.conversionSourceId &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                {formik.errors.conversionSourceId ? <div>{formik.errors.conversionSourceId as string}</div> : null}
              </FormHelperText>
            }
          </FormControl>}
        {canRenderDetails && showCurrentSelectionSummary &&
          <Box>
            <Typography variant='caption' color='text.secondary'>Selected emission factor</Typography>
            <Typography variant='body2' sx={{ mt: 0.25 }}>{selectionSummaryText}</Typography>
            <Link
              component='button'
              type='button'
              variant='body2'
              underline='hover'
              onClick={() => { setChangeSelectionRequested(true); setBrowseLevelsOpen(false); }}
              sx={{ cursor: 'pointer', mt: 0.5 }}>
              Change
            </Link>
          </Box>}
        {canRenderDetails && searchAvailable && !showCurrentSelectionSummary &&
          <Box>
            <Autocomplete<IFactorSearchOption, false, false, false>
              size={size}
              fullWidth
              options={factorOptions}
              value={retainedSelectionOption ?? displayedFactorOption}
              onChange={(event, option, reason) => {
                setSelectedFactorOption(option);
                setRecentFactorUnavailable(undefined);
                if (!option) {
                  if (reason === 'clear') clearFactorSelection();
                  return;
                }
                if (option.result) applyFactorSearchSelection(option.result);
                else if (option.recent) applyRecentFactorSelection(option.recent);
              }}
              onInputChange={(event, newInput, reason) => {
                if (reason === 'reset') return;
                setFactorSearchInput(newInput);
                setRecentFactorUnavailable(undefined);
              }}
              loading={factorSearchPending}
              loadingText='Searching emission factors.'
              filterOptions={(options) => options}
              isOptionEqualToValue={(option, value) => option.key === value.key}
              getOptionLabel={(option) => option.label}
              groupBy={(option) => option.group}
              noOptionsText={browsingCategory
                ? 'No emission factors for this category, source and year.'
                : showingRecents
                  ? 'Type at least two characters to search emission factors.'
                  : noMatchesText}
              renderOption={(props, option) => (
                <li {...props} key={option.key}>
                  <Box>
                    <Typography variant='body2'>{option.pathLabel || option.metaLabel}</Typography>
                    {option.pathLabel && option.metaLabel &&
                      <Typography variant='caption' color='text.secondary'>{option.metaLabel}</Typography>}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size={size}
                  label='Emission factor'
                  placeholder='e.g. natural gas kWh, diesel litres'
                  error={Boolean(recentFactorUnavailable) || selectionUnavailableForYear}
                  helperText={recentFactorUnavailable
                    ?? (selectionUnavailableForYear
                      ? `This factor is not published for ${calendarYear || 'this factor year'}. Choose another.`
                      : searchMatchedNothing
                        ? noMatchesText
                        : factorSearchHasMore ? 'Showing the first 50 matches. Keep typing to narrow the list.' : undefined)}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {factorSearchPending ? <CircularProgress color='inherit' size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {unitSelectControl && <Box sx={{ mt: 2 }}>{unitSelectControl}</Box>}
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              spacing={2}
              sx={{ mt: 1 }}
            >
              <Link
                component='button'
                type='button'
                variant='body2'
                underline='hover'
                onClick={() => setBrowseLevelsOpen(open => !open)}
                sx={{ cursor: 'pointer' }}>
                {browseLevelsOpen ? 'Hide categories' : 'Browse by category instead'}
              </Link>
              {browseRowTrailing}
            </Stack>
          </Box>}
        {canRenderDetails && !showCurrentSelectionSummary &&
          /* Collapsed, this still renders a zero-height element, and the parent form's spacing
             Stack gives every child a 16px margin-top. That put an invisible gap between the
             search box and the Amount field whenever the levels were hidden, which is the
             default. Drop the margin while it is hidden; it returns as soon as it opens. */
          <Collapse in={!searchAvailable || browseLevelsOpen} sx={{ '&.MuiCollapse-hidden': { mt: 0 } }}>
          <Stack direction={direction} spacing={2} sx={{ mt: searchAvailable ? 1.5 : 0 }}>
          {showLevelOneSelect &&
            <FormControl fullWidth size={size} variant='outlined'>
              <InputLabel>Factor Detail 1</InputLabel>
              <Select
                label={'Factor Detail 1'}
                size={size}
                disabled={disableFields}
                onOpen={() => { getFilterValues('detailLevelOne') }}
                value={detailLevelOne ?? ''}
                onChange={(e) => handleValueSelection('detailLevelOne', e.target.value as string)}
                sx={{
                  color: 'text.primary',
                }}>
                {detailLevelOneFilters?.length && detailLevelOneFilters?.length > 0 ? detailLevelOneFilters?.map((filter, index) => <MenuItem key={index} value={filter}>{filter}</MenuItem>) : <MenuItem value={detailLevelOne}>{detailLevelOne}</MenuItem>}
              </Select>
            </FormControl>}
          {(detailLevelTwo || detailLevelTwoFilters.length > 0) &&
            <FormControl fullWidth size={size} variant='outlined'>
              <InputLabel>Factor Detail 2</InputLabel>
              <Select
                label={'Factor Detail 2'}
                size={size}
                disabled={disableFields}
                onOpen={() => { getFilterValues('detailLevelTwo') }}
                value={detailLevelTwo ?? ''}
                onChange={(e) => handleValueSelection('detailLevelTwo', e.target.value as string)}
                sx={{
                  color: 'text.primary',
                }}>
                {detailLevelTwoFilters?.length && detailLevelTwoFilters?.length > 0 ? detailLevelTwoFilters?.map((filter, index) => <MenuItem key={index} value={filter}>{filter}</MenuItem>) : <MenuItem value={detailLevelTwo}>{detailLevelTwo}</MenuItem>}
              </Select>
            </FormControl>}
          {(detailLevelThree || detailLevelThreeFilters.length > 0) && <FormControl fullWidth size={size} variant='outlined'>
            <InputLabel>Factor Detail 3</InputLabel>
            <Select
              label={'Factor Detail 3'}
              size={size}
              disabled={disableFields}
              onOpen={() => { getFilterValues('detailLevelThree') }}
              value={detailLevelThree ?? ''}
              onChange={(e) => handleValueSelection('detailLevelThree', e.target.value as string)}
              sx={{
                color: 'text.primary',
              }}>
              {detailLevelThreeFilters?.length && detailLevelThreeFilters?.length > 0 ? detailLevelThreeFilters?.map((filter, index) => <MenuItem key={index} value={filter}>{filter}</MenuItem>) : <MenuItem value={detailLevelThree}>{detailLevelThree}</MenuItem>}
            </Select>
          </FormControl>}
          {(detailLevelFour || detailLevelFourFilters.length > 0) && <FormControl fullWidth size={size} variant='outlined'>
            <InputLabel>Factor Detail 4</InputLabel>
            <Select
              label={'Factor Detail 4'}
              size={size}
              disabled={disableFields}
              onOpen={() => { getFilterValues('detailLevelFour') }}
              value={detailLevelFour ?? ''}
              onChange={(e) => handleValueSelection('detailLevelFour', e.target.value as string)}
              sx={{
                color: 'text.primary',
              }}>
              {detailLevelFourFilters?.length && detailLevelFourFilters?.length > 0 ? detailLevelFourFilters?.map((filter, index) => <MenuItem key={index} value={filter}>{filter}</MenuItem>) : <MenuItem value={detailLevelFour}>{detailLevelFour}</MenuItem>}
            </Select>
          </FormControl>}
          {(detailLevelFive || detailLevelFiveFilters.length > 0) && <FormControl fullWidth size={size} variant='outlined'>
            <InputLabel>Factor Detail 5</InputLabel>
            <Select
              label={'Factor Detail 5'}
              size={size}
              disabled={disableFields}
              onOpen={() => { getFilterValues('detailLevelFive') }}
              value={detailLevelFive ?? ''}
              onChange={(e) => handleValueSelection('detailLevelFive', e.target.value as string)}
              sx={{
                color: 'text.primary',
              }}>
              {detailLevelFiveFilters?.length && detailLevelFiveFilters?.length > 0 ? detailLevelFiveFilters?.map((filter, index) => <MenuItem key={index} value={filter}>{filter}</MenuItem>) : <MenuItem value={detailLevelFive}>{detailLevelFive}</MenuItem>}
            </Select>
          </FormControl>}
          {!searchAvailable && unitSelectControl}
          </Stack>
        </Collapse>}
        {canRenderDetails && emptyStateMessage && detailLevelOneLoaded && !showLevelOneSelect && !showUnitSelect &&
          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
            {emptyStateMessage}
          </Typography>}
      </>
    )
  }